import { ActionableNotification, InlineLoading } from '@carbon/react';
import style from './structuring-module.module.scss';

interface DocumentMatchingBannerProps {
  title?: string;
  message?: string;
}

/**
 * Shown while matching runs, on the document or on a single party.
 *
 * @param {DocumentMatchingBannerProps} props - The banner title and message.
 * @return {JSX.Element} The running-match banner.
 */
export function DocumentMatchingBanner({
  title = 'Generating suggestions…',
  message = 'Matching is running. Suggestions will appear here when it finishes.',
}: DocumentMatchingBannerProps) {
  return (
    <div className={style.runningBanner} role="status" aria-live="polite">
      <div className={style.runningSpinner}>
        <InlineLoading description="" />
      </div>
      <span className={style.runningText}>
        <strong>{title}</strong> {message}
      </span>
    </div>
  );
}

interface DocumentSuggestionsBannerProps {
  running: boolean;
  onRun: () => void;
}

/**
 * A document whose parties have never been matched: offer the run, or show it
 * running.
 *
 * @param {DocumentSuggestionsBannerProps} props - Whether matching is running, and how to start it.
 * @return {JSX.Element} The run offer, or the running banner.
 */
export function DocumentSuggestionsBanner({
  running,
  onRun,
}: DocumentSuggestionsBannerProps) {
  if (running) return <DocumentMatchingBanner />;
  return (
    <ActionableNotification
      kind="info"
      lowContrast
      hideCloseButton
      inline
      title="No suggestions available yet"
      subtitle="Re-run Match to generate match suggestions for the parties in this document."
      actionButtonLabel="Re-run Match"
      onActionButtonClick={onRun}
      className={style.unstructuredBanner}
    />
  );
}
